/**
 * Revokes Google OAuth tokens by calling Google's OAuth2 revoke endpoint
 * @param accessToken - The Google OAuth access token
 * @param refreshToken - The Google OAuth refresh token (optional)
 * @returns Promise that resolves when the token is successfully revoked
 */
export const revokeGoogleOAuthToken = async (
  accessToken: string,
  refreshToken?: string
): Promise<void> => {
  // Prefer refresh token for revocation as it revokes all associated tokens
  const tokenToRevoke = refreshToken || accessToken;

  if (!tokenToRevoke) {
    console.warn('[GoogleOAuthRevoke] No token provided, skipping revoke');
    return;
  }

  console.log('[GoogleOAuthRevoke] Attempting to revoke token:', {
    tokenType: refreshToken ? 'refresh_token' : 'access_token',
    tokenLength: tokenToRevoke.length,
    tokenPrefix: tokenToRevoke.substring(0, 10) + '...',
  });

  try {
    // Use the newer recommended endpoint
    const response = await fetch('https://oauth2.googleapis.com/revoke', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `token=${encodeURIComponent(tokenToRevoke)}`,
    });

    // Log response details for debugging
    const responseText = await response.text();
    console.log('[GoogleOAuthRevoke] Response:', {
      status: response.status,
      statusText: response.statusText,
      body: responseText,
    });

    if (response.status === 400) {
      const errorData = JSON.parse(responseText);
      if (errorData.error === 'invalid_token') {
        // Token is already invalid or not revocable, which is acceptable
        console.log(
          '[GoogleOAuthRevoke] Token is already invalid or not revocable, continuing with logout'
        );
        return;
      }
    }

    if (!response.ok) {
      throw new Error(
        `Failed to revoke Google token: ${response.status} ${response.statusText} - ${responseText}`
      );
    }

    console.log('[GoogleOAuthRevoke] Token revoked successfully');
  } catch (error) {
    console.error('[GoogleOAuthRevoke] Failed to revoke token:', error);
    // Don't throw - allow logout to continue even if revoke fails
  }
};
